import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import "./Data.css";

/* ---------- COLORS ---------- */
const COLORS = {
  blue: "#60a5fa",
  orange: "#fbbf24",
  red: "#f87171",
  green: "#34d399",
  gray: "#94a3b8",
  purple: "#a78bfa",
};

/* ---------- LABEL FUNCTION ---------- */
const renderLabel = ({ name, percent }) =>
  `${name}: ${(percent * 100).toFixed(0)}%`;

/* ---------- DATA ---------- */
const pyramidData = [
  { age: "0-9", male: 18, female: 17 },
  { age: "10-19", male: 16, female: 15 },
  { age: "20-29", male: 10, female: 9 },
  { age: "30-39", male: 6, female: 5 },
  { age: "40+", male: 3, female: 3 },
];

const populationGrowth = [
  { year: "1950", value: 2.2 },
  { year: "2026", value: 9 },
  { year: "2050", value: 14.5 },
];

const gdpSplit = [
  { name: "Agriculture", value: 60 },
  { name: "Services", value: 28 },
  { name: "Mining", value: 12 },
  { name: "Manufacturing", value: 1.6 },
];

const energyAccess = [
  { name: "Urban", value: 30 },
  { name: "Rural", value: 5 },
];

const hungerData = [
  { year: "2000", value: 57.8 },
  { year: "2025", value: 28.5 },
];

const teacherData = [
  { name: "Qualified", value: 66 },
  { name: "Unqualified", value: 34 },
];

const surveyAge = [
  { name: "21-30", value: 58 },
  { name: "Others", value: 42 },
];

const scorecard = [
  { category: "Energy", goal: "Electricity Access", now: "36%", target: "78%" },
  { category: "Health", goal: "Maternal Mortality", now: "354/100k", target: "<70" },
  { category: "Agriculture", goal: "Food Insecurity", now: "18% Severe", target: "Zero Hunger" },
  { category: "Environment", goal: "Forest Cover", now: "<5%", target: "Increase" },
];

export default function Data() {
  return (
    <div className="dashboard-container">

      <h2 className="section-title">Demographics</h2>

      <div className="row">
        <div className="col-lg-6">
          <ChartCard title="Population Pyramid - Youth Wave">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pyramidData} layout="vertical">
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis dataKey="age" type="category" stroke="#94a3b8" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="male" fill={COLORS.blue} />
                <Bar dataKey="female" fill={COLORS.orange} />
              </BarChart>
            </ResponsiveContainer>
            <p className="highlight-text">Median Age: 19.9 — A Nation of the Future</p>
          </ChartCard>
        </div>

        <div className="col-lg-6">
          <ChartCard title="Population Growth Forecast">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={populationGrowth}>
                <CartesianGrid stroke="#1e293b" />
                <XAxis dataKey="year" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={tooltipStyle} />
                <Line dataKey="value" stroke={COLORS.green} strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
            <p className="highlight-text">Planning for +60% Growth</p>
          </ChartCard>
        </div>
      </div>

      <h2 className="section-title">Industry Deep Dive</h2>

      <div className="row">
        <div className="col-lg-6">
          <ChartCard title="GDP Split">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Tooltip contentStyle={tooltipStyle} />
                <Pie
                  data={gdpSplit}
                  dataKey="value"
                  nameKey="name"
                  label={renderLabel}
                >
                  {gdpSplit.map((_, i) => (
                    <Cell key={i} fill={Object.values(COLORS)[i]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="col-lg-6">
          <ChartCard title="Energy Access">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={energyAccess}>
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" fill={COLORS.red} />
              </BarChart>
            </ResponsiveContainer>
            <p className="highlight-text">91% rely on firewood & charcoal</p>
          </ChartCard>
        </div>
      </div>

      <h2 className="section-title">Social Welfare</h2>

      <div className="row">
        <div className="col-lg-6">
          <ChartCard title="Hunger Index Trend">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={hungerData}>
                <XAxis dataKey="year" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={tooltipStyle} />
                <Line dataKey="value" stroke={COLORS.red} strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="col-lg-6">
          <ChartCard title="Teacher Qualification">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Tooltip contentStyle={tooltipStyle} />
                <Pie
                  data={teacherData}
                  dataKey="value"
                  nameKey="name"
                  label={renderLabel}
                >
                  {teacherData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={i === 0 ? COLORS.green : COLORS.red}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      <h2 className="section-title">Citizen Survey</h2>

      <ChartCard title="Respondent Age Distribution">
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Tooltip contentStyle={tooltipStyle} />
            <Pie data={surveyAge} dataKey="value" label={renderLabel}>
              <Cell fill={COLORS.purple} />
              <Cell fill={COLORS.gray} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <p className="highlight-text">500+ Members Joined</p>
      </ChartCard>

      <ChartCard title="Top Priorities">
        <ol className="leaderboard">
          <li>Vocational Training</li>
          <li>Water & Sanitation</li>
          <li>Climate Vulnerability</li>
        </ol>
      </ChartCard>

      <h2 className="section-title">SEAS2050 Scorecard</h2>

      <div className="scorecard-grid">
        {scorecard.map((row, i) => (
          <div className="scorecard-item" key={i}>
            <div className="scorecard-header">
              <h4>{row.category}</h4>
              <span className="goal">{row.goal}</span>
            </div>

            <div className="scorecard-body">
              <div className="metric">
                <span className="label">Now</span>
                <span className="value current">{row.now}</span>
              </div>

              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>

              <div className="metric">
                <span className="label">2030 Target</span>
                <span className="value target">{row.target}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- TOOLTIP ---------- */
const tooltipStyle = {
  backgroundColor: "#1e293b",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
};

/* ---------- CARD ---------- */
function ChartCard({ title, children }) {
  return (
    <div className="chart-card">
      <h5>{title}</h5>
      {children}
    </div>
  );
}